import type {
  HubExampleStream,
  HubOneOfCreateCameraExampleStreamTriggermode,
} from '@robovision/hub'
import {
  addOrReplaceBy,
  arrayToObject,
} from '@robovision/rvai-utils'
import { defineStore } from 'pinia'
import {
  reactive,
  readonly,
  toRefs,
} from 'vue'
import type { EmulatorCameraData } from '~domain/cameras/CameraData'
import { DatapoolExampleStream } from '~domain/cameras/DatapoolExampleStream'
import { hubApi } from '~services/api'


interface ExampleStreamsState {
  datapoolExampleStreams: DatapoolExampleStream[],
  deleting: boolean,
  exampleStreams: Record<string, HubExampleStream>
}

export const useExampleStreamsStore = defineStore('exampleStreams', () => {
  const state = reactive<ExampleStreamsState>({
    datapoolExampleStreams: [],
    deleting: false as boolean,
    exampleStreams: {},
  }) as ExampleStreamsState

  const getters = {
    getDatapoolExampleStream (id: string): DatapoolExampleStream | undefined {
      return state.datapoolExampleStreams.find(stream => stream.id === id)
    },

    getDatapoolExampleStreams (): DatapoolExampleStream[] {
      return state.datapoolExampleStreams
    },

    getExampleStream (id: string): HubExampleStream | undefined {
      return state.exampleStreams[id]
    },

    getExampleStreamDeleting (): boolean {
      return state.deleting
    },
  }

  const actions = {
    async createCameraExampleStream (
      projectId: string,
      cameraId: string,
      triggerMode: HubOneOfCreateCameraExampleStreamTriggermode,
    ) : Promise<HubExampleStream> {
      const { data: exampleStream } = await hubApi.exampleStreams.createExampleStream({
        config: {
          cameraId,
          kind: 'CAMERA',
          triggerMode,
        },
        projectId,
      })
      state.exampleStreams[exampleStream.id] = exampleStream
      return exampleStream
    },

    async createDatapoolExampleStream (
      datapoolId: string,
      emulatorData: EmulatorCameraData,
    ) : Promise<HubExampleStream> {
      const { data: exampleStream } = await hubApi.exampleStreams.createExampleStream({
        config: {
          datapoolId,
          kind: 'DATAPOOL',
          ...emulatorData.tagId ? {
            sampleFilters: [{
              tagIds: [
                [emulatorData.tagId],
              ],
            }],
          } : null,
          simulatedFps: emulatorData.simulatedFps,
        },
        name: emulatorData.name,
        projectId: emulatorData.projectId,
      })

      state.datapoolExampleStreams.push(DatapoolExampleStream.fromResponse(exampleStream))
      return exampleStream
    },

    async deleteDatapoolExampleStreams (exampleStreams: DatapoolExampleStream[]): Promise<void> {
      state.deleting = true
      try {
        const deletedStreams = await Promise.all(
          exampleStreams.map(e => hubApi.exampleStreams.deleteExampleStream(e.id)),
        )
        const deletedStreamsIds = deletedStreams.map(c => c.data.id)
        state.datapoolExampleStreams = state.datapoolExampleStreams.filter(c => !deletedStreamsIds.includes(c.id))
      } finally {
        state.deleting = false
      }
    },

    async listDatapoolExampleStreams () : Promise<void> {
      const { data: exampleStreams } = await hubApi.exampleStreams.listExampleStreams({
        filter: [
          'kind:DATAPOOL',
        ],
      })

      state.datapoolExampleStreams = exampleStreams.items.map(DatapoolExampleStream.fromResponse)
    },

    async loadDatapoolExampleStream (exampleStreamId: string) : Promise<void> {
      try {
        const { data: exampleStream } = await hubApi.exampleStreams.getExampleStream(exampleStreamId)
        const newExampleStream = DatapoolExampleStream.fromResponse(exampleStream)

        state.datapoolExampleStreams = addOrReplaceBy(
          state.datapoolExampleStreams,
          newExampleStream,
          s => s.id === exampleStreamId,
        )
      } catch {}
    },

    setExampleStream (exampleStreams: HubExampleStream[]) : void {
      state.exampleStreams = {
        ...state.exampleStreams,
        ...arrayToObject(exampleStreams, e => e.id, e => e),
      }
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },
  }

  return {
    ...toRefs(readonly(state)),
    ...actions,
    ...getters,
  }
})
